import { useCallback, useEffect, useRef, useState } from "react";
import type { Columna, EstadoCriterio, Etapa, Veredicto } from "@/reclutamiento/tipos";
import { alReconectar, esErrorDeRed, MENSAJE_SIN_CONEXION, useEstadoRed } from "./red";

export type Semaforo = "ok" | "yel" | "red";

export const semDeVeredicto = (v: Veredicto): Semaforo =>
  v === "cumple" ? "ok" : v === "revisar" ? "yel" : "red";
export const semDeEstado = (e: EstadoCriterio): Semaforo =>
  e === "ok" ? "ok" : e === "falta" ? "yel" : "red";
export const ETIQUETA_ESTADO: Record<EstadoCriterio, string> = {
  ok: "Cumple",
  falta: "Por confirmar",
  no: "No cumple",
};
/** Marca que acompaña a cada columna (null = sin marca). */
export const MARCA_COLUMNA: Partial<Record<Columna, Semaforo>> = {
  revisar: "yel",
  descartado: "red",
  contratado: "ok",
};

// Preferencias del visitante (vista elegida, vacante). Nunca estado importante.
export function leerPref(clave: string): string | null {
  try {
    return localStorage.getItem("portal-pref-" + clave);
  } catch {
    return null;
  }
}
export function guardarPref(clave: string, valor: string) {
  try {
    localStorage.setItem("portal-pref-" + clave, valor);
  } catch {
    /* sin almacenamiento: solo no se recuerda */
  }
}

export function mensajeError(e: unknown, respaldo = "Algo falló. Intenta de nuevo."): string {
  // Una falla de conexión nunca se enseña cruda («TypeError: Failed to fetch»).
  if (esErrorDeRed(e)) return MENSAJE_SIN_CONEXION;
  if (e instanceof Error && e.message) return e.message.replace(/^(TypeError|Error):\s*/, "");
  if (typeof e === "string" && e) return e;
  return respaldo;
}

export async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const t = document.createElement("textarea");
      t.value = texto;
      t.style.position = "fixed";
      t.style.opacity = "0";
      document.body.appendChild(t);
      t.select();
      const ok = document.execCommand("copy");
      t.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

export type Carga<T> = {
  datos: T | null;
  /** Solo cuando NO hay nada que enseñar (si ya había datos, se siguen viendo mientras se reconecta). */
  error: string | null;
  cargando: boolean;
  /** Hay datos en pantalla pero la última vuelta falló por la red; se reintenta sola. */
  reconectando: boolean;
  recargar: () => void;
  setDatos: React.Dispatch<React.SetStateAction<T | null>>;
};

// ---------------------------------------------------------------- lo último bueno de cada vista
// En memoria y en sessionStorage (esta pestaña; se borra al cerrarla o al salir). Solo sirve para PINTAR
// mientras llega lo fresco o mientras regresa la red: lo fresco SIEMPRE se pide y lo reemplaza.
const PREFIJO_CACHE = "portal-cache:";
const memoria = new Map<string, unknown>();
function leerCache<T>(clave: string | null | undefined): T | null {
  if (!clave) return null;
  if (memoria.has(clave)) return memoria.get(clave) as T;
  try {
    const t = sessionStorage.getItem(PREFIJO_CACHE + clave);
    if (!t) return null;
    const v = JSON.parse(t) as T;
    memoria.set(clave, v);
    return v;
  } catch {
    return null;
  }
}
function guardarCache(clave: string | null | undefined, v: unknown) {
  if (!clave || v == null) return;
  memoria.set(clave, v);
  try {
    sessionStorage.setItem(PREFIJO_CACHE + clave, JSON.stringify(v));
  } catch {
    /* sin almacenamiento o lleno: se queda solo en memoria */
  }
}
/** Al salir: que el siguiente que entre en esta pestaña no vea nada del anterior. */
export function borrarCache() {
  memoria.clear();
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(PREFIJO_CACHE)) sessionStorage.removeItem(k);
    }
  } catch {
    /* nada que borrar */
  }
}
/** Clave de la vista, separada por cliente, usuario y «ver como» (nunca se cruzan datos de otro). */
export const claveCache = (
  s: { cliente: { id: string }; usuario: { correo: string } | null },
  verComo: string | null,
  vista: string,
) => [s.cliente.id, s.usuario?.correo ?? "-", verComo ?? "yo", vista].join("|");

/**
 * Carga asíncrona con estado de carga/error y recarga. `inicial` (opcional) da algo que pintar mientras llega
 * lo fresco (p. ej. la ficha precargada). Lo fresco SIEMPRE se pide y lo reemplaza.
 * Si la red falla: se enseña lo que ya estaba o, si no hay, lo último bueno guardado con `clave` (solo mientras
 * haya problemas de red: con red sana nunca se pinta algo viejo). Sin nada que enseñar, mensaje humano. En
 * ambos casos se reintenta sola cada ~20 s, al volver el foco y al regresar la red.
 */
export function useCarga<T>(fn: () => Promise<T>, deps: unknown[], inicial?: () => T | null, clave?: string | null): Carga<T> {
  const iniRef = useRef(inicial);
  iniRef.current = inicial;
  const claveRef = useRef(clave);
  claveRef.current = clave;
  const [propios, setPropios] = useState<T | null>(() => inicial?.() ?? null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallaRed, setFallaRed] = useState(false);
  const [vuelta, setVuelta] = useState(0);
  const red = useEstadoRed();
  const problemaRed = fallaRed || red.reintentando > 0 || red.sinConexion;
  // Lo último bueno de esta vista, solo si hay problemas de red y todavía no llegó nada.
  const datos = propios ?? (problemaRed ? leerCache<T>(clave) : null);
  const datosRef = useRef(datos);
  datosRef.current = datos;
  const depsCambiaron = useRef(false);
  const silenciosa = useRef(false);
  const enVuelo = useRef(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  // Todo lo que se pinta (incluido lo optimista) queda como «lo último bueno».
  const setDatos: React.Dispatch<React.SetStateAction<T | null>> = useCallback((v) => {
    setPropios((antes) => {
      const base = antes ?? datosRef.current;
      const nuevo = typeof v === "function" ? (v as (x: T | null) => T | null)(base) : v;
      guardarCache(claveRef.current, nuevo);
      return nuevo;
    });
  }, []);
  useEffect(() => {
    let vivo = true;
    // Datos de otras dependencias (otra vacante, otra ficha) no se muestran mientras llega lo nuevo.
    if (depsCambiaron.current) {
      setPropios(iniRef.current?.() ?? null);
      setError(null);
      setFallaRed(false);
    }
    depsCambiaron.current = true;
    const quieta = silenciosa.current;
    silenciosa.current = false;
    if (!quieta) setCargando(true);
    enVuelo.current = true;
    fnRef
      .current()
      .then((d) => {
        if (!vivo) return;
        setPropios(d);
        guardarCache(claveRef.current, d);
        setError(null);
        setFallaRed(false);
      })
      .catch((e) => {
        if (!vivo) return;
        const deRed = esErrorDeRed(e);
        setFallaRed(deRed);
        // Con algo que enseñar, una falla de red no lo tapa: se sigue viendo mientras se reconecta.
        const hay = datosRef.current != null || leerCache(claveRef.current) != null;
        setError(deRed && hay ? null : mensajeError(e, "No pudimos cargar esto."));
      })
      .finally(() => {
        if (!vivo) return;
        enVuelo.current = false;
        setCargando(false);
      });
    return () => {
      vivo = false;
      enVuelo.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, vuelta]);
  const recargar = useCallback(() => {
    depsCambiaron.current = false; // recargar conserva lo que ya se ve
    setVuelta((v) => v + 1);
  }, []);
  // Sin conexión: se vuelve a intentar sola (sin parpadear el esqueleto).
  useEffect(() => {
    if (!fallaRed) return;
    return alReconectar(() => {
      if (enVuelo.current) return;
      silenciosa.current = true;
      recargar();
    });
  }, [fallaRed, recargar]);
  return { datos, error, cargando, reconectando: fallaRed && datos != null, recargar, setDatos };
}

/** Publica la altura de un elemento como variable CSS en <html> (para lo que se pega debajo de él). */
export function useAltoComoVariable(ref: React.RefObject<HTMLElement | null>, variable: string) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const raiz = document.documentElement;
    const poner = () => raiz.style.setProperty(variable, `${Math.round(el.getBoundingClientRect().height)}px`);
    poner();
    const ro = new ResizeObserver(poner);
    ro.observe(el);
    return () => {
      ro.disconnect();
      raiz.style.removeProperty(variable);
    };
  }, [ref, variable]);
}

/** ¿La pantalla es angosta (celular)? */
export function useAngosto(px = 640): boolean {
  const q = `(max-width: ${px - 1}px)`;
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.matchMedia(q).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(q);
    const f = () => setM(mq.matches);
    f();
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, [q]);
  return m;
}

/** Etapa como la lee el cliente: «Nuevo» se confunde con «nuevo desde tu visita», así que se dice «Sin contactar». */
export const ETAPA_CLIENTE: Record<Etapa, string> = {
  nuevo: "Sin contactar",
  contactado: "Contactado",
  entrevistado: "Entrevistado",
  contratado: "Contratado",
  algun_dia: "Algún día / Tal vez",
  descartado: "Descartado",
};
